/**
 * Theme Automatic Gift — adds/removes a gift line when cart qualifying qty
 * meets Theme settings (collection + min quantity). Pattern matches delivery-date.js.
 */
if (!window.Maximize.loadedScript.includes('automatic-gift.js')) {
  window.Maximize.loadedScript.push('automatic-gift.js');

  (function () {
    const SKIP_KEY = 'theme_automatic_gift_skip';
    const DEBOUNCE_MS = 150;
    const PROPERTY_KEY_DEFAULT = '__THEME_automatic_gift';

    let cfg = null;
    let isUpdating = false;
    let debounceTimer = null;
    let lastGiftKeys = new Set();

    function getCfg() {
      return window.AutomaticGiftConfig || { enabled: false };
    }

    function root() {
      return window.Shopify?.routes?.root || '/';
    }

    function propertyKey() {
      return cfg?.propertyKey || PROPERTY_KEY_DEFAULT;
    }

    function debounce(fn, ms = DEBOUNCE_MS) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(fn, ms);
    }

    function shopNowParts(tz) {
      try {
        const fmt = new Intl.DateTimeFormat('en-CA', {
          timeZone: tz || undefined,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        });
        const parts = Object.fromEntries(
          fmt.formatToParts(new Date()).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value])
        );
        return {
          date: `${parts.year}-${parts.month}-${parts.day}`,
          time: `${parts.hour}:${parts.minute}`,
        };
      } catch (_) {
        const d = new Date();
        const pad = (n) => String(n).padStart(2, '0');
        return {
          date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
          time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
        };
      }
    }

    function isWithinSchedule() {
      const endDate = (cfg.endDate || '').trim();
      if (!endDate) return true;
      const endTime = ((cfg.endTime || '23:59').trim() || '23:59').slice(0, 5);
      const now = shopNowParts(cfg.ianaTimezone);
      const endStamp = `${endDate}T${endTime}`;
      const nowStamp = `${now.date}T${now.time}`;
      return nowStamp <= endStamp;
    }

    async function fetchCart() {
      const res = await fetch(`${root()}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('cart.js failed');
      return res.json();
    }

    function loadQualifiesFromDom() {
      const els = document.querySelectorAll('[data-automatic-gift-cart]');
      for (const el of els) {
        try {
          const data = JSON.parse(el.textContent);
          if (Array.isArray(data)) {
            const map = new Map();
            data.forEach((row) => {
              if (row?.key) map.set(row.key, !!row.qualifies);
            });
            return map;
          }
        } catch (_) {
          /* ignore */
        }
      }
      return null;
    }

    function qualifiesMapMatchesCart(map, cart) {
      if (!map) return false;
      const keys = (cart?.items || []).map((i) => i.key);
      if (map.size !== keys.length) return false;
      return keys.every((k) => map.has(k));
    }

    /** Section HTML carries Liquid collection membership; refresh if DOM lags cart.js. */
    async function ensureQualifiesMap(cart) {
      let map = loadQualifiesFromDom();
      if (qualifiesMapMatchesCart(map, cart)) return map;
      await rerenderCartUi(null);
      map = loadQualifiesFromDom();
      if (qualifiesMapMatchesCart(map, cart)) return map;
      // Fallback: initial snapshot (page load only).
      if (cfg.initialCart?.items) {
        map = new Map();
        cfg.initialCart.items.forEach((row) => {
          if (row?.key) map.set(row.key, !!row.qualifies);
        });
        if (qualifiesMapMatchesCart(map, cart)) return map;
      }
      return map || new Map();
    }

    function isGiftLine(item) {
      const props = item?.properties || {};
      return props[propertyKey()] != null && props[propertyKey()] !== '';
    }

    function giftLines(cart) {
      return (cart?.items || []).filter(isGiftLine);
    }

    function qualifyingQuantity(cart, qualifiesMap) {
      let qty = 0;
      (cart?.items || []).forEach((item) => {
        if (isGiftLine(item)) return;
        if (qualifiesMap?.get(item.key)) qty += item.quantity || 0;
      });
      return qty;
    }

    function desiredGiftQty() {
      const giftQty = Math.max(0, Number(cfg.giftQuantity) || 1);
      const maxPerOrder = Math.max(0, Number(cfg.maxPerOrder) || 1);
      return Math.min(giftQty, maxPerOrder);
    }

    function skipEnabled() {
      return cfg.skipAfterRemove !== false;
    }

    function readSkip() {
      try {
        return sessionStorage.getItem(SKIP_KEY) === '1';
      } catch (_) {
        return false;
      }
    }

    function writeSkip(on) {
      try {
        if (on) sessionStorage.setItem(SKIP_KEY, '1');
        else sessionStorage.removeItem(SKIP_KEY);
      } catch (_) {
        /* ignore */
      }
    }

    /** Same as coupon-code: main cart section on /cart, drawer section elsewhere. */
    function cartSectionId() {
      if (window.Maximize?.isCartPage) {
        return (
          document.getElementById('MainCart__Items')?.dataset?.sectionId ||
          document.getElementById('CartDrawer')?.dataset?.sectionId
        );
      }
      return document.getElementById('CartDrawer')?.dataset?.sectionId;
    }

    function sectionIds() {
      const sectionId = cartSectionId();
      const helper = window.xCartHelper;
      if (helper?.getSectionsToRender) {
        return [...new Set(helper.getSectionsToRender(sectionId).map((s) => s.id).filter(Boolean))];
      }
      try {
        const store = Alpine?.store?.('xCartHelper');
        if (store?.getSectionsToRender) {
          return [...new Set(store.getSectionsToRender(sectionId).map((s) => s.id).filter(Boolean))];
        }
      } catch (_) {
        /* ignore */
      }
      return sectionId ? [sectionId, 'cart-icon-bubble'] : ['cart-icon-bubble'];
    }

    function sectionsToRender() {
      const sectionId = cartSectionId();
      if (window.xCartHelper?.getSectionsToRender) {
        return window.xCartHelper.getSectionsToRender(sectionId);
      }
      try {
        return Alpine?.store?.('xCartHelper')?.getSectionsToRender?.(sectionId) || [];
      } catch (_) {
        return [];
      }
    }

    /** Authoritative open only — not `.is-visible` (left on during close animation). */
    function isDrawerOpen() {
      try {
        if (Alpine?.store?.('xMiniCart')?.isOpen) return true;
      } catch (_) {
        /* ignore */
      }
      const drawer = document.getElementById('CartDrawer');
      return !!(drawer && drawer.hasAttribute('open'));
    }

    function syncDrawerOpenChrome() {
      const open = isDrawerOpen();
      const wrapper = document.getElementById('CartDrawer__Wrapper');
      const upsell = document.getElementById('CartDrawer__Upsell');
      wrapper?.classList.toggle('is-visible', open);
      if (!open) upsell?.classList.remove('is-visible');
    }

    async function applySectionsHtml(sectionsHtmlById) {
      if (!sectionsHtmlById || typeof getSectionInnerHTML !== 'function') return false;
      const list = sectionsToRender();
      const drawer = document.getElementById('CartDrawer');
      // Snapshot before replace so an open drawer doesn't flash closed for a frame.
      const openBefore = isDrawerOpen();

      list.forEach((section) => {
        const el = document.querySelector(section.selector);
        const html = sectionsHtmlById[section.id];
        if (!el || !html) return;
        el.innerHTML = getSectionInnerHTML(html, section.selector);
        if (section.selector === '#CartDrawer' && openBefore) {
          el.querySelector('#CartDrawer__Wrapper')?.classList.add('is-visible');
        }
      });

      if (typeof Alpine !== 'undefined') {
        try {
          if (drawer) Alpine.initTree(drawer);
          const mainCart = document.getElementById('MainCart__Items');
          if (mainCart) Alpine.initTree(mainCart);
        } catch (_) {
          /* ignore */
        }
      }

      // ponytail: handleClose sets isOpen on rAF; wait one frame so we don't re-stick open chrome.
      await new Promise((r) => requestAnimationFrame(r));
      syncDrawerOpenChrome();
      return true;
    }

    async function rerenderCartUi(sectionsPayload) {
      if (sectionsPayload) {
        await applySectionsHtml(sectionsPayload);
      } else {
        const ids = sectionIds();
        if (!ids.length) return false;
        const res = await fetch(`${root()}?sections=${ids.join(',')}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) return false;
        await applySectionsHtml(await res.json());
      }
      try {
        const cart = await fetchCart();
        if (window.xCartHelper) window.xCartHelper.currentItemCount = cart.item_count || 0;
        try {
          const store = Alpine?.store?.('xCartHelper');
          if (store) store.currentItemCount = cart.item_count || 0;
        } catch (_) {
          /* ignore */
        }
        if (typeof window.xCartHelper?.updateCurrentCountItem === 'function') {
          window.xCartHelper.updateCurrentCountItem();
        }
      } catch (_) {
        /* ignore */
      }
      return true;
    }

    function emitCartUpdated() {
      document.dispatchEvent(
        new CustomEvent(
          typeof CART_EVENT !== 'undefined' ? CART_EVENT.cartUpdate : 'maximize:cart:updated',
          { bubbles: true, detail: { isItemCountChanged: true, fromAutomaticGift: true } }
        )
      );
    }

    async function addGift(qty) {
      const ids = sectionIds();
      const body = {
        items: [
          {
            id: cfg.giftVariantId,
            quantity: qty,
            properties: { [propertyKey()]: '1' },
          },
        ],
      };
      if (ids.length) body.sections = ids;

      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        console.warn('[automatic-gift] add failed', data);
        return null;
      }
      return data;
    }

    async function removeGiftLines(lines) {
      if (!lines.length) return null;
      const ids = sectionIds();
      const updates = {};
      lines.forEach((line) => {
        updates[line.key] = 0;
      });
      const body = { updates };
      if (ids.length) body.sections = ids;

      const res = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        console.warn('[automatic-gift] remove failed', data);
        return null;
      }
      return data;
    }

    async function setGiftQuantity(line, qty) {
      const ids = sectionIds();
      const body = { id: line.key, quantity: qty };
      if (ids.length) body.sections = ids;
      const res = await fetch(`${root()}cart/change.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        console.warn('[automatic-gift] change failed', data);
        return null;
      }
      return data;
    }

    /**
     * Modo limpieza: el regalo esta apagado pero el carrito todavia lo lleva.
     *
     * Pasa cuando termina una promocion y el cliente vuelve dias despues: sin
     * esto, su carrito conserva la garantia de 0 $ para siempre.
     *
     * Se quita POR LA PROPIEDAD, nunca por el id de producto. Una garantia que
     * el cliente compro de verdad no lleva `__THEME_automatic_gift`, asi que no
     * se toca. Borrar por producto le quitaria algo que ha pagado.
     */
    async function runCleanup() {
      if (isUpdating) return;

      let cart;
      try {
        cart = await fetchCart();
      } catch (e) {
        console.warn('[automatic-gift]', e);
        return;
      }

      const gifts = giftLines(cart);
      if (!gifts.length) return;

      isUpdating = true;
      try {
        const payload = await removeGiftLines(gifts);
        if (payload) {
          await rerenderCartUi(payload.sections || null);
          emitCartUpdated();
        }
      } catch (e) {
        console.warn('[automatic-gift] cleanup failed', e);
      } finally {
        isUpdating = false;
      }
    }

    async function runSync() {
      cfg = getCfg();
      if (!cfg.enabled) {
        // El regalo esta apagado. Si quedan lineas de regalo en el carrito, hay
        // que quitarlas; si no, no hay nada que hacer.
        if (cfg.cleanup) await runCleanup();
        return;
      }
      if (!cfg.giftVariantId || !cfg.collectionId) return;
      if (isUpdating) return;

      let cart;
      try {
        cart = await fetchCart();
      } catch (e) {
        console.warn('[automatic-gift]', e);
        return;
      }

      const qualifiesMap = await ensureQualifiesMap(cart);
      const gifts = giftLines(cart);
      const giftKeysNow = new Set(gifts.map((g) => g.key));
      const qualifyingQty = qualifyingQuantity(cart, qualifiesMap);
      const minQty = Math.max(1, Number(cfg.minQuantity) || 2);
      const wantQty = desiredGiftQty();
      const scheduleOk = isWithinSchedule();
      const shouldHaveGift = scheduleOk && qualifyingQty >= minQty && wantQty > 0;

      // Customer removed gift while still qualifying → skip until they stop qualifying.
      if (skipEnabled() && lastGiftKeys.size && !gifts.length && shouldHaveGift) {
        writeSkip(true);
      }
      if (!shouldHaveGift) {
        writeSkip(false);
      }
      lastGiftKeys = giftKeysNow;

      const skipped = skipEnabled() && readSkip();
      let payload = null;

      isUpdating = true;
      try {
        if (!shouldHaveGift) {
          if (gifts.length) payload = await removeGiftLines(gifts);
        } else if (skipped) {
          // do not re-add
        } else if (!gifts.length) {
          payload = await addGift(wantQty);
        } else {
          const totalGiftQty = gifts.reduce((sum, g) => sum + (g.quantity || 0), 0);
          if (totalGiftQty > wantQty) {
            // Trim extras: zero out all but first, set first to wantQty.
            const [keep, ...rest] = gifts;
            if (rest.length) await removeGiftLines(rest);
            payload = await setGiftQuantity(keep, wantQty);
          } else if (totalGiftQty < wantQty) {
            payload = await setGiftQuantity(gifts[0], wantQty);
          }
        }

        if (payload) {
          await rerenderCartUi(payload.sections || null);
          emitCartUpdated();
        }
      } catch (e) {
        console.warn('[automatic-gift] sync failed', e);
      } finally {
        isUpdating = false;
      }
    }

    function onCartUpdated(e) {
      if (e?.detail?.fromAutomaticGift) return;
      debounce(() => {
        void runSync();
      });
    }

    function bind() {
      cfg = getCfg();
      // En modo limpieza tambien hay que engancharse: el cliente puede llegar a
      // cualquier pagina, no solo al carrito. La sincronizacion inicial de abajo
      // se dispara en cada carga, que es lo que hace que la limpieza no dependa
      // de que abra el carrito.
      if (!cfg.enabled && !cfg.cleanup) return;

      // Seed last gift keys from initial cart so first sync doesn't false-trigger skip.
      (cfg.initialCart?.items || []).forEach((item) => {
        if (item?.properties && item.properties[propertyKey()] != null) {
          lastGiftKeys.add(item.key);
        }
      });

      document.addEventListener('maximize:cart:updated', onCartUpdated);
      document.addEventListener('cart:updated', onCartUpdated);

      document.addEventListener('alpine:init', () => {
        if (typeof Alpine === 'undefined') return;
        let wasOpen = false;
        Alpine.effect(() => {
          const open = !!Alpine.store('xMiniCart')?.isOpen;
          if (open && !wasOpen) debounce(() => void runSync());
          wasOpen = open;
        });
      });

      // Initial sync (page load with existing cart).
      debounce(() => void runSync(), 50);
    }

    window.AutomaticGiftTheme = {
      sync: () => runSync(),
      selfCheck() {
        const c = getCfg();
        console.assert(typeof c.enabled === 'boolean', 'enabled boolean');
        if (c.enabled) {
          console.assert(!!c.propertyKey, 'propertyKey');
          console.assert(typeof c.minQuantity === 'number', 'minQuantity');
        }
        console.info('[automatic-gift] selfcheck ok', c);
      },
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', bind);
    } else {
      bind();
    }

    if (new URLSearchParams(location.search).has('automatic_gift_selfcheck')) {
      window.AutomaticGiftTheme.selfCheck();
    }
  })();
}
